import { DEFAULT_HEADER_GROUP_ID } from "@/configs/system";
import {
	obterConfiguracaoEntidadeCatalogo,
	type CatalogListParams,
	type ConfiguracaoEntidadeCatalogo,
} from "@/configs/configCatalogo";
import type { PaginatedResponse } from "@/types/common";
import { fetchComRefreshToken } from "../../../comAutenticacaoSpace";
import { obterBaseUrlSpace } from "../../../clienteSpace";
import { getGlobalHeadersSpaceApi } from "../../../globalHeaders.spaceApi";
import { getGrupoAtualAutenticacaoSpaceApi } from "../../../sessaoAutenticacao.space";
import { definirParametroConsulta } from "../parametrosConsulta";

/**
 * Transporte do catálogo. Mora em `utils/catalogo/` — e não no topo de `control/` — porque
 * catálogo não é um microserviço: a mesma função consulta estoque, pessoas e utilitários,
 * conforme o `dominio` declarado em `@/configs/configCatalogo`.
 */

// ------------------------------- CONSTANTS -------------------------------
const CHAVES_PAGINACAO_FORMATO_LISTA = new Set(["page", "limit", "offset"]);

export const keys_catalogo = {
	base: ["space"] as const,
	catalogo: () => [...keys_catalogo.base, "catalogo"] as const,
	lookup: (entidade: string, parametros: CatalogListParams) =>
		[...keys_catalogo.catalogo(), "lookup", entidade, parametros] as const,
};

// --------------------------------- TYPES ---------------------------------
type EndpointCatalogoSeparado = {
	readonly endpointNormalizado: string;
	readonly parametrosConsulta: URLSearchParams;
};

// -------------------------------- HELPERS --------------------------------
function criarCabecalhosCatalogo(): Record<string, string> {
	const cabecalhosGlobais = getGlobalHeadersSpaceApi();
	const idGrupo = getGrupoAtualAutenticacaoSpaceApi();
	const cabecalhosRequisicao: Record<string, string> = { ...cabecalhosGlobais };

	if (idGrupo) cabecalhosRequisicao[DEFAULT_HEADER_GROUP_ID] = String(idGrupo);
	else delete cabecalhosRequisicao[DEFAULT_HEADER_GROUP_ID];

	return cabecalhosRequisicao;
}

function separarEndpointCatalogo(endpoint: string): EndpointCatalogoSeparado {
	const indiceInterrogacao = endpoint.indexOf("?");
	const endpointPathBruto = indiceInterrogacao >= 0 ? endpoint.slice(0, indiceInterrogacao) : endpoint;
	const endpointQueryBruta = indiceInterrogacao >= 0 ? endpoint.slice(indiceInterrogacao + 1) : "";

	return {
		endpointNormalizado: endpointPathBruto.replace(/^\/+/, ""),
		parametrosConsulta: new URLSearchParams(endpointQueryBruta),
	};
}

function construirUrlListagemCatalogo(
	urlBaseApi: string | undefined,
	endpointNormalizado: string,
	parametrosConsulta: URLSearchParams,
): string {
	let baseNormalizada = String(urlBaseApi ?? "");
	while (baseNormalizada.endsWith("/")) baseNormalizada = baseNormalizada.slice(0, -1);

	const queryString = parametrosConsulta.toString();
	const sufixoConsulta = queryString ? `?${queryString}` : "";

	return `${baseNormalizada}/${endpointNormalizado}${sufixoConsulta}`;
}

function aplicarParametrosConsultaCatalogo(
	parametrosConsulta: URLSearchParams,
	parametros: CatalogListParams | ConfiguracaoEntidadeCatalogo["parametrosFixos"],
	chavesIgnoradas?: ReadonlySet<string>,
): void {
	if (!parametros) return;

	Object.entries(parametros).forEach(([chave, valor]) => {
		if (chavesIgnoradas?.has(chave)) return;
		definirParametroConsulta(parametrosConsulta, chave, valor);
	});
}

function normalizarRespostaListaCatalogo<T>(itens: T[]): PaginatedResponse<T> {
	return {
		items: itens,
		total: itens.length,
		offset: 0,
		limit: itens.length,
		has_next: false,
		has_previous: false,
		total_pages: 1,
		current_page: 1,
	};
}

function construirParametrosPaginaEmpresasPorGrupo(
	idGrupoEmpresa: number,
	pagina: number,
	limite: number,
): CatalogListParams {
	return {
		...construirParametrosEmpresasPorGrupo(idGrupoEmpresa),
		page: pagina,
		limit: limite,
	};
}

function consolidarRespostaEmpresasPorGrupo<T>(
	primeiraPagina: PaginatedResponse<T>,
	itens: T[],
	totalPaginas: number,
): PaginatedResponse<T> {
	return {
		...primeiraPagina,
		items: itens,
		total: primeiraPagina.total ?? itens.length,
		offset: 0,
		limit: itens.length,
		has_next: false,
		has_previous: false,
		total_pages: totalPaginas,
		current_page: 1,
	};
}

// ------------------------------- FUNCTIONS -------------------------------
export function normalizarParametrosCatalogo(parametros?: CatalogListParams): CatalogListParams {
	return {
		...parametros,
		page: parametros?.page ?? 1,
		limit: parametros?.limit ?? 100,
	};
}

export function construirParametrosEmpresasPorGrupo(idGrupoEmpresa: number): CatalogListParams {
	return {
		id_cad_grupo_empresa__conteudo: idGrupoEmpresa,
		id_cad_grupo_empresa__operador: "eq",
	};
}

export async function listarItensCatalogo<T = unknown>(
	entidade: string,
	parametros?: CatalogListParams,
	signal?: AbortSignal,
): Promise<PaginatedResponse<T> | null> {
	const configuracao: ConfiguracaoEntidadeCatalogo = obterConfiguracaoEntidadeCatalogo(entidade);
	const { rota, endpointListagem, dominio, parametrosFixos, formatoResposta = "paginado" } = configuracao;

	const endpoint = endpointListagem ?? `${rota}/listar`;
	const { endpointNormalizado, parametrosConsulta } = separarEndpointCatalogo(endpoint);
	const chavesIgnoradas = formatoResposta === "lista" ? CHAVES_PAGINACAO_FORMATO_LISTA : undefined;

	// A ordem mantém a precedência: query do endpoint → parâmetros informados → parâmetros fixos.
	aplicarParametrosConsultaCatalogo(parametrosConsulta, parametros, chavesIgnoradas);
	aplicarParametrosConsultaCatalogo(parametrosConsulta, parametrosFixos);

	const urlRequisicao = construirUrlListagemCatalogo(
		obterBaseUrlSpace(dominio),
		endpointNormalizado,
		parametrosConsulta,
	);
	const resposta = await fetchComRefreshToken(urlRequisicao, {
		signal,
		credentials: "include",
		headers: criarCabecalhosCatalogo(),
	});

	if (!resposta.ok) {
		console.error(`Erro ao listar ${entidade}:`, resposta.statusText);
		return null;
	}

	const corpoResposta = await resposta.json();

	if (formatoResposta === "lista" && Array.isArray(corpoResposta))
		return normalizarRespostaListaCatalogo(corpoResposta as T[]);

	return corpoResposta as PaginatedResponse<T>;
}

export async function listarTodasEmpresasPorGrupo<T = unknown>(
	idGrupoEmpresa: number,
	limit = 100,
): Promise<PaginatedResponse<T> | null> {
	const primeiraPagina = await listarItensCatalogo<T>(
		"empresas",
		construirParametrosPaginaEmpresasPorGrupo(idGrupoEmpresa, 1, limit),
	);

	if (!primeiraPagina) return null;

	const totalPaginas = Math.max(primeiraPagina.total_pages ?? 1, 1);
	const itensConsolidados: T[] = [...(primeiraPagina.items ?? [])];

	for (let pagina = 2; pagina <= totalPaginas; pagina += 1) {
		const respostaPagina = await listarItensCatalogo<T>(
			"empresas",
			construirParametrosPaginaEmpresasPorGrupo(idGrupoEmpresa, pagina, limit),
		);

		if (!respostaPagina) break;

		itensConsolidados.push(...(respostaPagina.items ?? []));
	}

	return consolidarRespostaEmpresasPorGrupo(primeiraPagina, itensConsolidados, totalPaginas);
}
